import React, { useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useHistory } from "react-router-dom";
import { useAuth } from "../../../../../context/AuthProvider";
import { useLocalization } from "../../../../../context/LocalizeContextProvider";
import { getItemOptions } from "../../../../../services/Handlers/MasterItemsService";
import { getPackages } from "../../../../../services/Handlers/MasterPackageService";
import {
  findItemById,
  formatNumberWithCurrency,
  getParams,
} from "../../../../../utils/utils";
import PharmacyDetailComponents from "../components";
import { getActiveVisitSingle } from "../../../../../services/ActiveVisitService";
import {
  getDetailReservation,
  putUpdateReservationStatus,
} from "../../../../../services/Handlers/ReservationService";
import { useNotification } from "../../../../../context/NotificationProvider";
import {
  paymentItemType,
  statusReservationType,
} from "../../../../../utils/constants";
import { postSaveItemsPayment } from "../../../../../services/Handlers/PaymentService";
import { usePushNotifications } from "../../../../../context/PushNotificationProvider";
import { useModal } from "../../../../../context/ModalProvider";

const emptyPaymentItem = {
  package: null,
  item: null,
  qty: 1,
  amount: null,
  typeDiscount: null,
  discount: null,
  isPackage: false,
};

export default function PharmacyDetail() {
  const { userDetail } = useAuth();
  const notification = useNotification();
  const localize = useLocalization();
  const history = useHistory();
  const modal = useModal();
  const { refetchPharmacyCount } = usePushNotifications();

  const businessId = userDetail?.businessId;
  const bookingId = getParams("id");
  const visitId = getParams("visit_id");
  const prevValueSignatureRef = useRef("");

  // State management
  const [itemOptions, setItemOptions] = useState([]);
  const [packageOptions, setPackageOptions] = useState([]);
  const [paymentData, setPaymentData] = useState({
    paymentItems: [emptyPaymentItem],
  });
  const [visitData, setVisitData] = useState({
    visitId: null,
    patientId: null,
    patientName: "Unknown Patient",
    patientGender: "Unknown",
    doctorName: "Unknown Doctor",
    anamnesis: null,
    physicalExamination: null,
    diagnoses: [],
    uploadedFiles: [],
  });
  const [dataDetailReservation, setDataDetailReservation] = useState({
    id: null,
    paymentId: null,
    payment: null,
    createdBy: null,
    updatedBy: null,
    status: null,
  });

  // Fetch item options
  const { isLoading: isLoadingItems } = useQuery(
    ["GetItemsOption", businessId],
    () => getItemOptions(businessId),
    {
      onSuccess: (res) => {
        if (res.data && res.status === 200) {
          setItemOptions(res.data);
        }
      },
      enabled: !!businessId,
      refetchOnWindowFocus: false,
    }
  );

  // Fetch package options
  const { isLoading: isLoadingPackages } = useQuery(
    ["PackageList", businessId],
    () => getPackages({ businessId: businessId, size: 999, active: true }),
    {
      onSuccess: (res) => {
        if (res.status === 200 || res.status === 201) {
          setPackageOptions(res?.data.data || []);
        }
      },
      enabled: !!businessId,
    }
  );

  const {
    isLoading: isLoadingUpdateStatus,
    mutate: mutateUpdateReservationStatus,
  } = useMutation(putUpdateReservationStatus, {
    onSuccess: (res) => {
      if (res.status === 200 || res.status === 201) {
        refetchPharmacyCount();
        modal.dismiss();
        setTimeout(() => {
          history.goBack();
        }, 200);
        notification.success(localize.getText("successPrepareMedicine"));
      } else {
        notification.error(
          res?.data?.detail || localize.getText("unknownMessageError")
        );
      }
    },
    onError: () => {
      notification.error(localize.getText("unknownMessageError"));
    },
  });

  useQuery(
    ["ActiveVisitSingle", visitId],
    () => getActiveVisitSingle(visitId),
    {
      onSuccess: (res) => {
        const response = res.data;
        if (response) {
          let diagnoses = response?.diagnosisJson || [];
          if (Array.isArray(diagnoses) && diagnoses.length > 0) {
            diagnoses = diagnoses.map((item) => item.label);
          }

          setVisitData({
            visitId: visitId,
            patientId: response?.patient?.id || null,
            patientName: response?.patient?.name || "Unknown Patient",
            patientGender: response?.patient?.gender,
            doctorName: response?.doctorName || "Unknown Doctor",
            anamnesis: response.anamnesis,
            physicalExamination: response.examination,
            diagnoses: diagnoses || [],
            uploadedFiles: response?.fileUploads || [],
          });
        }
      },
      refetchOnWindowFocus: false,
    }
  );

  const { isFetching: isFetchingDetail } = useQuery(
    ["DetailReservation", bookingId],
    () => getDetailReservation(bookingId),
    {
      onSuccess: (res) => {
        if (res.status === 200 || res.status === 201) {
          const response = res.data;
          if (response) {
            setDataDetailReservation((prevState) => ({
              ...prevState,
              id: response?.id,
              payment: response?.payment,
              paymentId: response?.payment?.id,
              createdBy: response?.payment?.created_by,
              updatedBy: response?.payment?.updated_by,
              status: response?.status,
            }));
          }
        } else {
          notification.error(localize.getText("unknownMessageError"));
        }
      },
      enabled: !!bookingId,
    }
  );

  const { isLoading: isLoadingSubmit, mutate: mutateSubmit } = useMutation(
    postSaveItemsPayment,
    {
      onSuccess: (res) => {
        if (res.status === 200 || res.status === 201) {
          notification.success(localize.getText("successAddPayment"));
          refetchPharmacyCount();
          setTimeout(() => {
            history.goBack();
          }, 200);
        } else {
          notification.error(
            res?.data?.detail || localize.getText("unknownMessageError")
          );
        }
      },
      onError: (res) => {
        notification.error(localize.getText("unknownMessageError"));
      },
    }
  );

  useEffect(() => {
    const payment = dataDetailReservation?.payment;

    if (!payment || !Array.isArray(payment?.payment_items)) {
      return;
    }

    const valueSignature = JSON.stringify({
      payment_items: payment.payment_items,
    });

    const hasValueChanged = valueSignature !== prevValueSignatureRef.current;
    const hasPackageItems = payment.payment_items.some(
      (item) => item.is_package
    );

    const canProcess =
      itemOptions.length > 0 && (!hasPackageItems || !isLoadingPackages);

    if (
      canProcess &&
      ((paymentData.paymentItems[0].item === null &&
        paymentData.paymentItems[0].package === null) ||
        hasValueChanged)
    ) {
      const paymentItems = [];

      if (
        payment.payment_items.filter(
          (value) => value.type !== paymentItemType.CORRECTION
        ).length > 0
      ) {
        payment.payment_items
          .filter((value) => value.type !== paymentItemType.CORRECTION)
          .forEach((item) => {
            let selectedPackage = null;

            if (item.is_package) {
              const findSelectedPackage = packageOptions.find(
                (pck) => pck.id === item.package_id
              );

              if (findSelectedPackage) {
                selectedPackage = {
                  value: findSelectedPackage.id,
                  label: findSelectedPackage.name,
                };
              }
            }

            paymentItems.push({
              item: findItemById(
                itemOptions,
                item.item_id || item.deposit_item_id
              ),
              package: selectedPackage,
              isPackage: item.is_package || false,
              isDeposit: item.is_deposit || false,
              qty: item.is_package ? 1 : item.quantity,
              amount: item.amount,
              typeDiscount: item.type_discount,
              discount: item.discount,
              additionalId: item.package_id || item.deposit_id,
              isRedo: false,
            });
          });
      } else {
        paymentItems.push(emptyPaymentItem);
      }

      setPaymentData({
        paymentItems,
      });

      prevValueSignatureRef.current = valueSignature;
    }
  }, [
    dataDetailReservation?.payment,
    itemOptions,
    packageOptions,
    isLoadingPackages,
    paymentData.paymentItems,
  ]);

  // Add new payment item
  const handleAddPaymentItem = () => {
    const dataPaymentItems = [...paymentData.paymentItems];
    dataPaymentItems.push({ ...emptyPaymentItem });

    setPaymentData((prevState) => ({
      ...prevState,
      paymentItems: dataPaymentItems,
    }));
  };

  // Add new payment package
  const handleAddPaymentPackage = () => {
    const dataPaymentItems = [...paymentData.paymentItems];
    dataPaymentItems.push({ ...emptyPaymentItem, isPackage: true });

    setPaymentData((prevState) => ({
      ...prevState,
      paymentItems: dataPaymentItems,
    }));
  };

  // Delete payment item
  const handleDeletePaymentItem = (index) => {
    const dataPaymentItems = [...paymentData.paymentItems];
    dataPaymentItems.splice(index, 1);

    setPaymentData((prevState) => ({
      ...prevState,
      paymentItems: dataPaymentItems,
    }));
  };

  // Handle form changes
  const handleChangePaymentForm = (name, value, nameItem, indexItem) => {
    const dataPayment = JSON.parse(JSON.stringify({ ...paymentData }));

    if (!nameItem && indexItem === undefined) {
      dataPayment[name] = value;
    } else {
      dataPayment[name].forEach((item, index) => {
        if (index === indexItem) {
          if (nameItem === "item") {
            const selectedItem = findItemById(itemOptions, value.value);
            if (selectedItem) {
              item["amount"] = selectedItem.price;
            }
          }
          if (nameItem === "package") {
            const selectedPackage = packageOptions.find(
              (pck) => pck.id === value.value
            );
            if (selectedPackage) {
              item["amount"] = selectedPackage.price;
              item["qty"] = 1;
            }
          }
          if (nameItem === "qty") {
            const selectedItem = findItemById(itemOptions, item["item"]?.value);
            if (selectedItem) {
              item["amount"] = selectedItem.price * value;
            }
          }

          item[nameItem] = value;
        }
      });
    }
    setPaymentData(dataPayment);
  };

  // Get discount items for display
  const getDiscountItems = (paymentItems) => {
    return paymentItems
      .filter((item) => item.discount && item.typeDiscount)
      .map((item) => ({
        name: item.isPackage
          ? `${localize.getText("package")} ${item.package?.label || ""}`
          : `${localize.getText("item")} ${item.item?.label || ""}`,
        discount:
          item.typeDiscount === "NUMBER"
            ? formatNumberWithCurrency(-item.discount)
            : `${item.discount}%`,
      }));
  };

  // Calculate total amount
  const getTotalAmount = (paymentItems) => {
    return paymentItems
      .filter((item) => item.amount !== null)
      .reduce((sum, current) => {
        const amount = Number(current.amount) || 0;
        const discountType = current.typeDiscount;
        const discount = Number(current.discount) || 0;

        let discountedAmount = amount;

        if (discountType === "PERCENTAGE" && discount > 0) {
          discountedAmount -= (amount * discount) / 100;
        } else if (discountType === "NUMBER" && discount > 0) {
          discountedAmount -= discount;
        }

        return sum + Math.max(discountedAmount, 0);
      }, 0);
  };

  // Handle form submission
  const handleSubmit = async () => {
    const dataPaymentItems = paymentData.paymentItems.map((item) => ({
      ...item,
      itemId: item?.item?.value || null,
      itemName: item?.item?.label || null,
      packageId: item?.package?.value || null,
      packageName: item?.package?.label || null,
      type: paymentItemType.CONSULTATION,
    }));
    const totalAmount = paymentData.paymentItems
      .filter((item) => item.amount !== null)
      .reduce((sum, current) => {
        const amount = Number(current.amount) || 0;
        const discountType = current.typeDiscount;
        const discount = Number(current.discount) || 0;

        let discountedAmount = amount;

        if (discountType === "PERCENTAGE" && discount > 0) {
          discountedAmount -= (amount * discount) / 100;
        } else if (discountType === "NUMBER" && discount > 0) {
          discountedAmount -= discount;
        }

        return sum + Math.max(discountedAmount, 0);
      }, 0);

    const formattedData = {
      bookingId,
      paymentId: dataDetailReservation?.paymentId,
      createdBy: dataDetailReservation?.createdBy || userDetail?.firstName,
      updatedBy: userDetail?.firstName,
      totalAmount: totalAmount,
      paymentItems: dataPaymentItems,
      createdById: userDetail?.id,
    };

    mutateSubmit(formattedData);
  };

  const handleSubmitPayment = () => {
    mutateUpdateReservationStatus({
      status: statusReservationType.MEDICINE_READY,
      booking_id: null,
      visit_id: visitData.visitId,
    });
  };

  // Form validation
  const requiredArray = (value) =>
    value && value.length > 0 ? undefined : localize.getText("required");

  return (
    <PharmacyDetailComponents
      dataDetailReservation={dataDetailReservation}
      // Medical data
      medicalData={visitData}
      // Payment data and options
      paymentData={paymentData}
      itemOptions={itemOptions}
      packageOptions={packageOptions}
      // Loading states
      isLoadingItems={isLoadingItems}
      isLoadingPackages={isLoadingPackages}
      loading={isFetchingDetail || isLoadingSubmit || isLoadingUpdateStatus}
      // Event handlers
      handleAddPaymentItem={handleAddPaymentItem}
      handleAddPaymentPackage={handleAddPaymentPackage}
      handleDeletePaymentItem={handleDeletePaymentItem}
      handleChangePaymentForm={handleChangePaymentForm}
      handleSubmit={handleSubmit}
      // Utility functions
      getDiscountItems={getDiscountItems}
      getTotalAmount={getTotalAmount}
      requiredArray={requiredArray}
      // Localization
      localize={localize}
      handleSubmitPayment={handleSubmitPayment}
    />
  );
}
